import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ProjectFormData } from '../types/project-form';
import { projectService } from '../services/api';
import '../styles/Dashboard.css';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  useTheme,
  Container,
  Button,
  Paper,
  Chip,
  Alert,
} from '@mui/material';

import FolderIcon from '@mui/icons-material/Folder';
import AddIcon from '@mui/icons-material/Add';
import DescriptionIcon from '@mui/icons-material/Description';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { Project, ProjectStatus } from '../types/project';
import { getCurrentUser } from '../services/userService';
import { User, UserRole } from '../types/user';
import TopBar from '../components/TopBar';

interface StatusCard {
  title: string;
  count: number;
  color: string;
}

interface DashboardProps {
  onLogout: () => void;
}

const Dashboard: React.FC<DashboardProps> = ({ onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const [projects, setProjects] = useState<Project[]>([]);
  const [drafts, setDrafts] = useState<ProjectFormData[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentUser] = useState(getCurrentUser());
  const [successMessage, setSuccessMessage] = useState<string>('');

  useEffect(() => {
    // Fetch projects from API
    const fetchProjects = async () => {
      setLoading(true);
      try {
        // Fetch projects using our API service
        const allProjects = await projectService.getAllProjects();
        
        // Filter projects based on user's jurisdiction
        const filteredProjects = filterProjectsByJurisdiction(allProjects, currentUser);
        setProjects(filteredProjects);
      } catch (error) {
        console.error('Error fetching projects from API:', error);
        
        // Try local storage as first fallback
        try {
          const localProjects = localStorage.getItem('projects');
          if (localProjects) {
            const parsedProjects = JSON.parse(localProjects);
            setProjects(parsedProjects);
          } else {
            // Use mock data as last resort
            setProjects([{
              id: '1',
              programmeArea: 'HIV Prevention',
              subcategory: 'Testing',
              department: 'Department 1',
              projectCategory: 'Process Improvement',
              projectTitle: 'Project 1',
              responsiblePerson: 'User 1',
              participants: [],
              problemStatement: 'Problem 1',
              goalStatement: 'Goal 1',
              indicatorDescription: 'Indicator 1',
              useCustomIndicator: false,
              indicatorNumerator: 'Numerator 1',
              indicatorDenominator: 'Denominator 1',
              improvementFrom: 0,
              improvementTo: 100,
              problemIdentificationTool: 'decisionMatrix',
              rootCauseAnalysisTool: 'fishbone',
              measurementFrequency: 'WEEKLY',
              firstCycleDate: '2024-01-01',
              projectEndDate: '2024-02-01',
              initiativeSource: 'Source 1',
              status: 'ongoing',
              createdAt: '2024-01-01',
              updatedAt: '2024-01-01',
              facility: 'Facility 1',
              description: 'Description 1',
              startDate: '2024-01-01',
              endDate: '2024-02-01',
              testCycles: [],
              comments: [],
              resources: [],
              feedback: [],
              lastModified: '2024-01-01',
              createdBy: 'User 1'
            }]);
          }
        } catch (e) {
          console.error('Error parsing projects from localStorage:', e);
          setProjects([{
            id: '1',
            programmeArea: 'HIV Prevention',
            subcategory: 'Testing',
            department: 'Department 1',
            projectCategory: 'Process Improvement',
            projectTitle: 'Project 1',
            responsiblePerson: 'User 1',
            participants: [],
            problemStatement: 'Problem 1',
            goalStatement: 'Goal 1',
            indicatorDescription: 'Indicator 1',
            useCustomIndicator: false,
            indicatorNumerator: 'Numerator 1',
            indicatorDenominator: 'Denominator 1',
            improvementFrom: 0,
            improvementTo: 100,
            problemIdentificationTool: 'decisionMatrix',
            rootCauseAnalysisTool: 'fishbone',
            measurementFrequency: 'WEEKLY',
            firstCycleDate: '2024-01-01',
            projectEndDate: '2024-02-01',
            initiativeSource: 'Source 1',
            status: 'ongoing',
            createdAt: '2024-01-01',
            updatedAt: '2024-01-01',
            facility: 'Facility 1',
            description: 'Description 1',
            startDate: '2024-01-01',
            endDate: '2024-02-01',
            testCycles: [],
            comments: [],
            resources: [],
            feedback: [],
            lastModified: '2024-01-01',
            createdBy: 'User 1'
          }]);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  useEffect(() => {
    // Fetch draft projects from database
    const fetchDrafts = async () => {
      try {
        const draftProjects = await projectService.getProjectsByStatus('draft');
        // Convert Project[] to ProjectFormData[] for compatibility
        const formattedDrafts = draftProjects.map((project: any) => ({
          ...project,
          id: project.id || project._id || Math.random().toString(36).substring(2, 11)
        })) as ProjectFormData[];
        setDrafts(formattedDrafts);
      } catch (error) {
        console.error('[Dashboard] Error fetching draft projects:', error);
        // Fallback to localStorage if API fails
        const savedDrafts = JSON.parse(localStorage.getItem('projectDrafts') || '[]');
        setDrafts(savedDrafts);
      }
    };

    fetchDrafts();
    
    // Also refresh drafts when returning from AddProject with draftSaved flag
    if (location.state?.draftSaved) {
      fetchDrafts();
      
      // Show success message if provided
      if (location.state?.message) {
        setSuccessMessage(location.state.message);
        // Clear message after 5 seconds
        setTimeout(() => setSuccessMessage(''), 5000);
      }
      
      // Clear the state to prevent repeated refreshes
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, navigate, location.pathname]);

  const handleDraftClick = (draftId: string) => {
    navigate(`/add-project?draft=${draftId}`);
  };

  const isWithinOneWeek = (date: string) => {
    if (!date) return false;
    
    const endDate = new Date(date);
    const now = new Date();
    const diffDays = Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    
    // Project is approaching completion if:
    // 1. It's not past due (diffDays >= 0)
    // 2. It's within the next 7 days (diffDays <= 7)
    return diffDays >= 0 && diffDays <= 7;
  };

  // Filter projects based on user's jurisdiction
  const filterProjectsByJurisdiction = (allProjects: Project[], user: User | null) => {
    // If no user is logged in or user data is incomplete, return no projects for security
    if (!user) return [];
    
    
    // CQI_MANAGER and NATIONAL_USER have system-wide access
    if (user.role === UserRole.CQI_MANAGER || user.role === UserRole.NATIONAL_USER) {
      return allProjects;
    }
    
    // COUNTY_USER must have a valid county assigned
    if (user.role === UserRole.COUNTY_USER) {
      if (!user.county) {
        console.warn('[Dashboard] County user has no county assigned - showing no projects');
        return [];
      }
      return allProjects.filter(project => 
        project.county && project.county.toLowerCase() === user.county?.toLowerCase()
      );
    }
    
    // SUBCOUNTY_USER must have a valid subcounty assigned
    if (user.role === UserRole.SUBCOUNTY_USER) {
      if (!user.subCounty) {
        console.warn('[Dashboard] Subcounty user has no subcounty assigned - showing no projects');
        return [];
      }
      return allProjects.filter(project => 
        project.subcounty && project.subcounty.toLowerCase() === user.subCounty?.toLowerCase()
      );
    }
    
    // FACILITY_USER must have a valid facility assigned
    if (user.role === UserRole.FACILITY_USER) {
      if (!user.facility) {
        console.warn('[Dashboard] Facility user has no facility assigned - showing no projects');
        return [];
      }
      return allProjects.filter(project => 
        project.facility && project.facility.toLowerCase() === user.facility?.toLowerCase()
      );
    }
    
    // Default case - unknown role type or missing data - show no projects for security
    console.warn('[Dashboard] User has unrecognized role or missing data - showing no projects');
    return [];
  };

  const projectsByStatus = {
    // Only include ongoing projects that are not near completion
    ongoing: projects.filter((p) => 
      p.status === 'ongoing' as ProjectStatus && 
      !isWithinOneWeek(p.projectEndDate || p.endDate)
    ),
    
    // Near completion: ongoing projects that end within one week (but not past due)
    nearCompletion: projects.filter((p) => 
      p.status === 'ongoing' as ProjectStatus && 
      isWithinOneWeek(p.projectEndDate || p.endDate)
    ),
    
    completed: projects.filter((p) => p.status === 'completed' as ProjectStatus),
    archived: projects.filter((p) => p.status === 'archived' as ProjectStatus),
    draft: projects.filter((p) => p.status === 'draft' as ProjectStatus),
  } as const;

  const handleCardClick = (status: string) => {
    // Handle specific card types
    if (status === 'Ongoing Projects') {
      navigate('/projects/ongoing', { 
        state: { 
          projects: projectsByStatus.ongoing,
          title: 'Ongoing Projects',
          filteredByJurisdiction: true
        } 
      });
    } else if (status === 'Completed Projects') {
      navigate('/projects/completed', { 
        state: { 
          projects: projectsByStatus.completed,
          title: 'Completed Projects',
          filteredByJurisdiction: true
        } 
      });
    } else if (status === 'Shared on a Learning Forum') {
      // Navigate to archived projects (shared in learning forum)
      navigate('/projects/learning-forum', { 
        state: { 
          projects: projectsByStatus.archived,
          title: 'Learning Forum - Shared Projects',
          filteredByJurisdiction: true
        } 
      });
    }
    
  };

  const statusCards: StatusCard[] = [
    {
      title: 'Ongoing Projects',
      count: projectsByStatus.ongoing.length,
      color: '#FF9800', // Orange
    },
    {
      title: 'Completed Projects',
      count: projectsByStatus.completed.length,
      color: '#4CAF50', // Green
    },
    {
      title: 'Shared on a Learning Forum',
      // Count archived projects (those shared in learning forum)
      count: projectsByStatus.archived.length,
      color: '#2196F3', // Blue
    },
  ];

  const quickActions = [
    // Only show User Management to admin users (CQI_MANAGER)
    ...(currentUser?.role === UserRole.CQI_MANAGER ? [
      { title: 'User Management', icon: <FolderIcon />, path: '/user-management' }
    ] : [])
  ];

  return (
    <Box sx={{ flexGrow: 1 }}>

      <TopBar onLogout={onLogout}/>

      {/* Success Message */}
      {successMessage && (
        <Container maxWidth="lg" sx={{ mt: 2 }}>
          <Alert severity="success" onClose={() => setSuccessMessage('')}>
            {successMessage}
          </Alert>
        </Container>
      )}

      {currentUser && (
        <Container maxWidth="lg" sx={{ mt: 2 }}>
          <Paper sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }} elevation={1}>
            <LocationOnIcon color="primary" />
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {currentUser.role === UserRole.CQI_MANAGER && 'Admin - Full System Access'}
                {currentUser.role === UserRole.NATIONAL_USER && 'Kenya'}
                {currentUser.role === UserRole.COUNTY_USER && `${currentUser.county || 'No County Assigned'}`}
                {currentUser.role === UserRole.SUBCOUNTY_USER && (
                  <>
                    {`${currentUser.subCounty || 'No Subcounty Assigned'}`}
                    {currentUser.county && <>, {currentUser.county}</>}
                  </>
                )}
                {currentUser.role === UserRole.FACILITY_USER && (
                  <>
                    {`${currentUser.facility || 'No Facility Assigned'}`}
                    {currentUser.subCounty && <>, {currentUser.subCounty}</>}
                    {currentUser.county && <>, {currentUser.county}</>}
                  </>
                )}
              </Typography>
            </Box>
          </Paper>
        </Container>
      )}

      <Container maxWidth="xl" sx={{ mt: 4, mb: 4, textAlign: 'right' }}>
        <Grid container spacing={4}>
          {statusCards.map((card, index) => (
            <Grid key={index} item xs={12} sm={6} md={4}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
                <Card
                  onClick={() => handleCardClick(card.title)}
                  sx={{
                    height: '100%',
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    cursor: 'pointer',
                    p: 2,
                  '&:hover': {
                    boxShadow: 6,
                    transform: 'scale(1.02)',
                    transition: 'all 0.2s ease-in-out'
                  }
                }}
              >
                <CardContent sx={{ display: 'flex', width: '100%', justifyContent: 'flex-end', alignItems: 'center', px: 3 }}>
                  <Typography variant="h3" component="p" sx={{ color: card.color, fontWeight: 'bold', fontSize: '2.5rem', mr: 4 }}>
                    {card.count}
                  </Typography>
                  <Typography variant="h6" component="h2">
                    {card.title}
                  </Typography>
                </CardContent>
              </Card>
              </Box>
            </Grid>
          ))}
        </Grid>

        {quickActions.length > 0 && (
          <Paper sx={{ p: 3, mt: 4 }} elevation={0} variant="outlined">
            <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
              Quick Actions
            </Typography>
            <Grid container spacing={2}>
              {quickActions.map((action, index) => (
              <Grid key={index} component="div" sx={{ gridColumn: { xs: 'span 12', sm: 'span 6', md: 'span 3' } }}>
                <Button
                  variant="outlined"
                  startIcon={action.icon}
                  onClick={() => navigate(action.path)}
                  fullWidth
                  sx={{
                    py: 2,
                    height: '100%',
                    justifyContent: 'flex-start',
                    textAlign: 'left',
                  }}
                >
                  {action.title}
                </Button>
              </Grid>
            ))}
          </Grid>
        </Paper>
        )}

        {/* New QI Project Button - Only show for facility users */}
        {currentUser?.role === UserRole.FACILITY_USER && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              startIcon={<AddIcon />}
              onClick={() => navigate('/add-project')}
              sx={{ 
                py: 2, 
                px: 4,
                fontSize: '1.1rem',
                fontWeight: 'bold'
              }}
            >
              NEW QI PROJECT
            </Button>
          </Box>
        )}

        {currentUser?.role === UserRole.FACILITY_USER && (
        <Paper
          onClick={() => navigate('/projects/drafts', { 
            state: { 
              projects: projectsByStatus.draft,
              title: 'Draft Projects',
              filteredByJurisdiction: true
            } 
          })}
          sx={{
            p: 2,
            mt: 4,
            display: 'flex',
            alignItems: 'center',
            backgroundColor: theme.palette.grey[50],
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: theme.palette.grey[100],
              boxShadow: 1,
              transition: 'all 0.2s ease-in-out'
            }
          }}
          elevation={0}
        >
          <DescriptionIcon sx={{ mr: 2, color: theme.palette.text.secondary }} />
          <Box>
            <Typography variant="subtitle2" color="textSecondary">
              Saved as Draft
            </Typography>
            <Typography variant="h6" component="p">
              {projectsByStatus.draft.length}{' '}
              {projectsByStatus.draft.length === 1 ? 'Project' : 'Projects'}
            </Typography>
          </Box>
        </Paper>
        )}

        <Paper
          onClick={() => navigate('/resources')}
          sx={{
            p: 2,
            mt: 4,
            display: 'flex',
            alignItems: 'center',
            backgroundColor: theme.palette.grey[50],
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: theme.palette.grey[100],
              boxShadow: 1,
              transition: 'all 0.2s ease-in-out'
            }
          }}
          elevation={0}
        >
          <FolderIcon sx={{ mr: 2, color: theme.palette.text.secondary }} />
          <Box>
            <Typography variant="subtitle2" color="textSecondary">
              Resources
            </Typography>
            <Typography variant="h6" component="p">
              View Published Resources
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default Dashboard;
